import { HangoutState } from '../state/HangoutState.js';
import { getChatbotDisplayManager } from './chatbot-display-manager.js';
import { toggleSendMicButton } from './chat-input.js';
import { getCurrentLayoutMode, LAYOUT_MODES } from './layout-mode.js';
import { getTextboxDisplayManager } from './textbox-display-manager.js';
import { getTranscriptForLayout } from '../shared/history-resolution.js';

export class MessageDisplayManager {
    constructor() {
        this.initialized = false;
        this.displayManager = null;
        this.layoutMode = LAYOUT_MODES.VN;
    }

    async initialize() {
        if (this.initialized) {
            return this;
        }

        this.layoutMode = getCurrentLayoutMode();
        await this._setBackendForMode(this.layoutMode);
        this.initialized = true;
        return this;
    }

    async _setBackendForMode(mode) {
        const nextMode = mode === LAYOUT_MODES.CHATBOT ? LAYOUT_MODES.CHATBOT : LAYOUT_MODES.VN;
        const previousBackend = this.displayManager;
        const isSwitchFromChatbotToVn = this.layoutMode === LAYOUT_MODES.CHATBOT && nextMode === LAYOUT_MODES.VN;

        if (nextMode === LAYOUT_MODES.CHATBOT) {
            this._ensureChatbotComposerVisible();
            this._clearConversationSpeakingClasses();
            const { conversationQueue } = await import('./conversation-queue.js');
            conversationQueue?.clearQueue?.();
            this.displayManager = await getChatbotDisplayManager();
            const hangoutState = HangoutState.getInstance();
            const history = getTranscriptForLayout(hangoutState);
            await this.displayManager.hydrateFromHistory?.(history);
            this.displayManager.showCanvas?.();
            previousBackend?.hideCanvas?.();
        } else if (isSwitchFromChatbotToVn) {
            this.displayManager = await getTextboxDisplayManager();
            this.layoutMode = LAYOUT_MODES.VN;
            await this._hydrateVnRoundFromChatbotSwitch();
            this._ensureVnComposerVisibleForInput(this.displayManager);
            this.displayManager.showCanvas?.();
            previousBackend?.hideCanvas?.();
            return;
        } else {
            this.displayManager = await getTextboxDisplayManager();
            this._ensureVnComposerHidden(this.displayManager);
            await this._syncVnLatestMessageFromHistory();
            this.displayManager.showCanvas?.();
            previousBackend?.hideCanvas?.();
        }

        this.layoutMode = nextMode;
    }

    async _hydrateVnRoundFromChatbotSwitch() {
        if (!this.displayManager || typeof this.displayManager.createMessage !== 'function') return;

        const history = getTranscriptForLayout(HangoutState.getInstance());
        if (!Array.isArray(history) || history.length === 0) return;

        const { storageManager } = await import('../../../core/storage/index.js');
        const { getLatestRoundTurnCharacters } = await import('../shared/turn-navigation.js');
        const { refreshVnTurnNavigationFromLatestRound } = await import('./message-utils.js');

        const hangoutConfig = await storageManager.getItem('currentHangoutConfig') || {};
        const activeCharacters = hangoutConfig.allCharacters || [];
        const roundCharacters = getLatestRoundTurnCharacters(history, activeCharacters);

        let lastUserIndex = -1;
        for (let i = history.length - 1; i >= 0; i -= 1) {
            if (history[i]?.role === 'user') {
                lastUserIndex = i;
                break;
            }
        }
        const roundStart = lastUserIndex >= 0 ? lastUserIndex + 1 : 0;

        for (const character of roundCharacters) {
            let roundMessage = null;
            for (let i = history.length - 1; i >= roundStart; i -= 1) {
                const message = history[i];
                if (message?.role !== 'assistant') continue;
                if (String(message.characterId) !== String(character.id)) continue;
                if (typeof message.content !== 'string' || !message.content.trim()) continue;
                roundMessage = message;
                break;
            }
            if (!roundMessage) continue;

            await this.displayManager.createMessage(
                roundMessage.content,
                roundMessage.characterName || character.name || 'Character',
                character.id,
                null,
                { skipSpriteFocus: true }
            );
        }

        const latestSpeakerId = roundCharacters.length > 0
            ? roundCharacters[roundCharacters.length - 1].id
            : null;

        await refreshVnTurnNavigationFromLatestRound(latestSpeakerId, {
            skipSpriteFocus: true
        });
    }

    _setComposerForceHidden(hidden) {
        const chatInputContainer = document.querySelector('.chat-input-container');
        const floatingButton = document.getElementById('mic-send-button-floating');
        const mobileFloatingControls = document.getElementById('mobile-floating-controls');
        chatInputContainer?.classList.toggle('chat-hidden-force', hidden);
        floatingButton?.classList.toggle('chat-hidden-force', hidden);
        mobileFloatingControls?.classList.toggle('chat-hidden-force', hidden);
    }

    _ensureVnComposerVisibleForInput(vnDisplayManager) {
        this._setComposerForceHidden(false);

        if (vnDisplayManager && typeof vnDisplayManager._setMode === 'function') {
            vnDisplayManager._setMode('input', { source: 'user', preserveStageSprite: true });
        }

        toggleSendMicButton();
    }

    _clearConversationSpeakingClasses() {
        const hangoutContainer = document.getElementById('hangout-container');
        hangoutContainer?.classList.remove('conversation-active');
        document.querySelectorAll('.currently-speaking').forEach((element) => {
            element.classList.remove('currently-speaking');
        });
    }

    async _syncVnLatestMessageFromHistory() {
        if (!this.displayManager || typeof this.displayManager.createMessage !== 'function') return;
        const history = getTranscriptForLayout(HangoutState.getInstance());
        if (!Array.isArray(history) || history.length === 0) return;
        const latestAssistantMessage = history
            .slice()
            .reverse()
            .find((message) =>
                message
                && message.role === 'assistant'
                && message.characterId
                && typeof message.content === 'string'
                && message.content.trim() !== ''
            );
        if (!latestAssistantMessage) return;
        await this.displayManager.createMessage(
            latestAssistantMessage.content,
            latestAssistantMessage.characterName || 'Character',
            latestAssistantMessage.characterId
        );
    }

    _ensureChatbotComposerVisible() {
        this._setComposerForceHidden(false);
        toggleSendMicButton();
    }

    _ensureVnComposerHidden(vnDisplayManager) {
        this._setComposerForceHidden(true);

        if (vnDisplayManager && typeof vnDisplayManager._setMode === 'function') {
            vnDisplayManager._setMode('text');
        }
    }

    async setLayoutMode(mode) {
        if (mode === LAYOUT_MODES.NONE) return;
        const nextMode = mode === LAYOUT_MODES.CHATBOT ? LAYOUT_MODES.CHATBOT : LAYOUT_MODES.VN;
        if (this.layoutMode === nextMode && this.displayManager) return;
        await this._setBackendForMode(nextMode);
    }

    getLayoutMode() {
        return this.layoutMode;
    }

    async syncHistory(messages) {
        if (!this.displayManager) return;
        if (typeof this.displayManager.hydrateFromHistory === 'function') {
            await this.displayManager.hydrateFromHistory(messages);
        }
    }

    setTurnNavigation(characters, options = {}) {
        if (!this.displayManager) return;
        if (typeof this.displayManager.setTurnNavigation === 'function') {
            this.displayManager.setTurnNavigation(characters, options);
        }
    }

    getDisplayedCharacterId() {
        return this.displayManager?.displayedCharacterId ?? null;
    }

    syncMobilePortraitCharacterFocus(characterId) {
        if (!this.displayManager) return;
        if (typeof this.displayManager._syncMobilePortraitCharacterFocus === 'function') {
            this.displayManager._syncMobilePortraitCharacterFocus(characterId);
        }
    }

    isAvailable() {
        return this.displayManager?.isAvailable?.() || false;
    }

    async createMessage(messageText, characterName, characterId, spriteState, options = {}) {
        if (!this.displayManager) {
            console.warn('MessageDisplayManager: No display manager available');
            return null;
        }
        return await this.displayManager.createMessage(
            messageText,
            characterName,
            characterId,
            spriteState,
            options
        );
    }

    async createUserMessage(messageText, options = {}) {
        if (!this.displayManager || typeof this.displayManager.createUserMessage !== 'function') {
            return null;
        }
        return await this.displayManager.createUserMessage(messageText, options);
    }

    async createTypingIndicator(characterId, characterName, options = {}) {
        if (!this.displayManager) return null;
        return (await this.displayManager.createTypingIndicator?.(characterId, characterName, options)) || null;
    }

    async createRosterNotice(message) {
        if (!this.displayManager || typeof this.displayManager.createRosterNotice !== 'function') {
            return null;
        }
        return await this.displayManager.createRosterNotice(message);
    }

    findTextboxByCharacterId(characterId, options = {}) {
        if (!this.displayManager) return null;
        return this.displayManager.findTextboxByCharacterId?.(characterId, options) || null;
    }

    removeTextboxByCharacterId(characterId) {
        if (!this.displayManager) return false;
        if (typeof this.displayManager.removeTextboxByCharacterId !== 'function') return false;
        return this.displayManager.removeTextboxByCharacterId(characterId);
    }

    async removeTypingIndicator(characterId) {
        if (!this.displayManager) return;
        if (typeof this.displayManager.removeTypingIndicator === 'function') {
            await this.displayManager.removeTypingIndicator(characterId);
        }
    }

    updateMessageText(characterId, newText, options = {}) {
        if (!this.displayManager) return;
        this.displayManager.updateTextboxText(characterId, newText, options);
    }

    findMessageByCharacterId(characterId, options = {}) {
        if (!this.displayManager) return null;
        return this.displayManager.findTextboxByCharacterId(characterId, options);
    }

    bindPathToLatestAssistantMessage(characterId, path, options = {}) {
        if (!this.displayManager) return null;
        if (typeof this.displayManager.bindPathToLatestAssistantMessage !== 'function') return null;
        return this.displayManager.bindPathToLatestAssistantMessage(characterId, path, options);
    }

    async refreshMessageAvatar(characterId, spriteState, options = {}) {
        if (!this.displayManager) return null;
        if (typeof this.displayManager.refreshMessageAvatar === 'function') {
            return await this.displayManager.refreshMessageAvatar(characterId, spriteState, options);
        }
        return null;
    }

    async refreshMessageAvatarByPath(path, characterId, spriteState) {
        if (!this.displayManager) return null;
        if (typeof this.displayManager.refreshMessageAvatarByPath === 'function') {
            return await this.displayManager.refreshMessageAvatarByPath(path, characterId, spriteState);
        }
        return null;
    }

    getActiveMessages() {
        if (!this.displayManager) return [];
        return this.displayManager.getActiveTextboxes();
    }

    setActiveMessages(messages) {
        if (!this.displayManager) return;
        this.displayManager.setActiveTextboxes(messages);
    }

    async clearActiveMessages() {
        if (!this.displayManager) return;
        await this.displayManager.clearActiveTextboxes();
    }

    clearAllMessages() {
        if (!this.displayManager) return;
        this.displayManager.clearAllTextboxes();
    }

    hasActiveMessages() {
        if (!this.displayManager) return false;
        return this.displayManager.hasActiveTextboxes();
    }

    startAnimation() {
        this.displayManager?.startAnimation?.();
    }

    async getCharacterDisplayIndex(characterId) {
        if (!this.displayManager) return 0;
        if (typeof this.displayManager.getCharacterDisplayIndex === 'function') {
            return await this.displayManager.getCharacterDisplayIndex(characterId);
        }
        return 0;
    }

    redrawAllMessages() {
        if (!this.displayManager) return;
        if (typeof this.displayManager.redrawAllMessages === 'function') {
            this.displayManager.redrawAllMessages();
        }
    }

    cleanupInvalidMessages(validCharacterIds) {
        this.displayManager?.cleanupInvalidMessages?.(validCharacterIds);
    }

    showCanvas() {
        if (!this.displayManager) return;
        if (typeof this.displayManager.showCanvas === 'function') {
            this.displayManager.showCanvas();
        }
    }

    hideCanvas() {
        if (!this.displayManager) return;
        if (typeof this.displayManager.hideCanvas === 'function') {
            this.displayManager.hideCanvas();
        }
    }

    handleResize() {
        const dm = this.displayManager;
        if (typeof dm?.handleCanvasResize === 'function') {
            dm.handleCanvasResize();
        } else {
            dm?.handleResize?.();
        }
    }

    cleanup() {
        if (this.displayManager && typeof this.displayManager.cleanup === 'function') {
            this.displayManager.cleanup();
        }
        this.displayManager = null;
        this.initialized = false;
    }
}

let messageDisplayManagerInstance = null;

export async function getMessageDisplayManager() {
    if (!messageDisplayManagerInstance) {
        messageDisplayManagerInstance = new MessageDisplayManager();
        await messageDisplayManagerInstance.initialize();
    }
    return messageDisplayManagerInstance;
}
